// components/patient/PilotEnrollment.jsx
import React, { useState } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Icon } from '../ui/Icon';
import { useBrand } from '../../contexts/BrandContext';
import { US_STATES } from '../../utils/constants';

export function PilotEnrollment({ onNavigate }) {
  const { brand } = useBrand();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    age: '',
    state: '',
    hasMedicare: '',
    prescriptionCount: ''
  });
  const [eligible, setEligible] = useState(null);
  
  const handleCheck = () => {
    const age = parseInt(formData.age);
    const count = parseInt(formData.prescriptionCount);
    
    const isEligible = 
      age >= 25 && 
      age <= 64 && 
      formData.state && 
      formData.hasMedicare === 'no' &&
      count >= 2;
    
    setEligible(isEligible);
    setStep(2);
  };
  
  return (
    <div style={{
      minHeight: '100vh',
      background: '#F9FAFB',
      padding: '40px 24px',
      fontFamily: 'Libre Franklin, sans-serif'
    }}>
      <div style={{ maxWidth: 700, margin: '0 auto' }}>
        <Button
          variant="ghost"
          icon="arrowLeft"
          onClick={() => onNavigate('landing')}
          style={{ marginBottom: 24 }}
        >
          Back to Home
        </Button>
        
        {step === 1 ? (
          <Card>
            <div style={{ textAlign: 'center', marginBottom: 32 }}>
              <div style={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                background: `${brand.c.accent}15`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px'
              }}>
                <Icon name="award" size={36} color={brand.c.accent} />
              </div>
              <h1 style={{ fontSize: 32, fontWeight: 700, marginBottom: 12, color: brand.c.navy }}>
                Pilot Program Enrollment
              </h1>
              <p style={{ fontSize: 16, color: '#6B7280', lineHeight: 1.6 }}>
                Check if you qualify for our early access program with exclusive savings
              </p>
            </div>
            
            <div style={{
              background: '#EFF6FF',
              padding: 20,
              borderRadius: 12,
              marginBottom: 32
            }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 12, color: '#1E40AF' }}>
                Pilot Program Benefits
              </h3>
              <ul style={{ margin: 0, paddingLeft: 20, color: '#3B82F6', lineHeight: 1.8 }}>
                <li>Additional 10% savings on all medications</li>
                <li>Priority customer support</li>
                <li>Free express shipping on all orders</li>
                <li>Early access to new medications</li>
              </ul>
            </div>
            
            <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 20, color: brand.c.navy }}>
              Eligibility Check
            </h3>
            
            <Input
              label="Your Age"
              type="number"
              value={formData.age}
              onChange={e => setFormData({ ...formData, age: e.target.value })}
              placeholder="Enter your age"
              required
            />
            
            <Input
              label="State of Residence"
              type="select"
              value={formData.state}
              onChange={e => setFormData({ ...formData, state: e.target.value })}
              options={US_STATES}
              required
            />
            
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 600 }}>
                Do you have Medicare coverage? <span style={{ color: '#EF4444' }}>*</span>
              </label>
              <div style={{ display: 'flex', gap: 16 }}>
                <label style={{
                  flex: 1,
                  padding: 16,
                  border: `2px solid ${formData.hasMedicare === 'yes' ? brand.c.accent : '#E5E7EB'}`,
                  borderRadius: 8,
                  cursor: 'pointer',
                  textAlign: 'center',
                  transition: 'all 0.2s',
                  background: formData.hasMedicare === 'yes' ? `${brand.c.accent}05` : 'white'
                }}>
                  <input
                    type="radio"
                    name="medicare"
                    value="yes"
                    checked={formData.hasMedicare === 'yes'}
                    onChange={e => setFormData({ ...formData, hasMedicare: e.target.value })}
                    style={{ marginRight: 8 }}
                  />
                  Yes
                </label>
                <label style={{
                  flex: 1,
                  padding: 16,
                  border: `2px solid ${formData.hasMedicare === 'no' ? brand.c.accent : '#E5E7EB'}`,
                  borderRadius: 8,
                  cursor: 'pointer',
                  textAlign: 'center',
                  transition: 'all 0.2s',
                  background: formData.hasMedicare === 'no' ? `${brand.c.accent}05` : 'white'
                }}>
                  <input
                    type="radio"
                    name="medicare"
                    value="no"
                    checked={formData.hasMedicare === 'no'}
                    onChange={e => setFormData({ ...formData, hasMedicare: e.target.value })}
                    style={{ marginRight: 8 }}
                  />
                  No
                </label>
              </div>
            </div>
            
            <Input
              label="Number of Regular Prescriptions"
              type="number"
              value={formData.prescriptionCount}
              onChange={e => setFormData({ ...formData, prescriptionCount: e.target.value })}
              placeholder="How many prescriptions do you take regularly?"
              help="Count all medications you take on an ongoing basis"
              required
            />
            
            <Button
              fullWidth
              onClick={handleCheck}
              disabled={!formData.age || !formData.state || !formData.hasMedicare || !formData.prescriptionCount}
            >
              Check Eligibility
            </Button>
            
            <div style={{ textAlign: 'center', marginTop: 24 }}>
              <span style={{ fontSize: 14, color: '#6B7280' }}>
                Already have an account?{' '}
                <span
                  onClick={() => onNavigate('login')}
                  style={{
                    color: brand.c.accent,
                    fontWeight: 600,
                    cursor: 'pointer',
                    textDecoration: 'underline'
                  }}
                >
                  Log in
                </span>
              </span>
            </div>
          </Card>
        ) : (
          <Card>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                width: 96,
                height: 96,
                borderRadius: '50%',
                background: eligible ? '#D1FAE5' : '#FEE2E2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px'
              }}>
                <Icon 
                  name={eligible ? 'checkCircle' : 'x'} 
                  size={48} 
                  color={eligible ? '#10B981' : '#EF4444'} 
                />
              </div>
              
              {eligible ? (
                <>
                  <h2 style={{ fontSize: 28, fontWeight: 700, marginBottom: 16, color: '#10B981' }}>
                    🎉 You're Eligible!
                  </h2>
                  <p style={{ fontSize: 16, color: '#6B7280', marginBottom: 32, lineHeight: 1.6 }}>
                    Congratulations! You qualify for our pilot program. Create your account to start saving 
                    with exclusive pricing and benefits.
                  </p>
                  
                  <div style={{
                    background: '#D1FAE5',
                    padding: 24,
                    borderRadius: 12,
                    marginBottom: 32,
                    textAlign: 'left'
                  }}>
                    <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, color: '#065F46' }}>
                      Your Pilot Benefits
                    </h3>
                    <div style={{ display: 'grid', gap: 12 }}>
                      <div style={{ display: 'flex', gap: 12, alignItems: 'start' }}>
                        <Icon name="check" size={20} color="#10B981" />
                        <span style={{ fontSize: 14, color: '#065F46' }}>
                          <strong>90% average savings</strong> vs retail (10% extra on top of standard pricing)
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: 12, alignItems: 'start' }}>
                        <Icon name="check" size={20} color="#10B981" />
                        <span style={{ fontSize: 14, color: '#065F46' }}>
                          <strong>Free express shipping</strong> on all orders (usually $9.99)
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: 12, alignItems: 'start' }}>
                        <Icon name="check" size={20} color="#10B981" />
                        <span style={{ fontSize: 14, color: '#065F46' }}>
                          <strong>Priority support</strong> with dedicated pharmacist access
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: 12, alignItems: 'start' }}>
                        <Icon name="check" size={20} color="#10B981" />
                        <span style={{ fontSize: 14, color: '#065F46' }}>
                          <strong>Locked-in pricing</strong> - Your pilot rate never increases
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    <Button fullWidth icon="arrowRight" onClick={() => onNavigate('register')}>
                      Create Account & Enroll
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <h2 style={{ fontSize: 28, fontWeight: 700, marginBottom: 16, color: '#EF4444' }}>
                    Not Eligible Yet
                  </h2>
                  <p style={{ fontSize: 16, color: '#6B7280', marginBottom: 32, lineHeight: 1.6 }}>
                    Unfortunately, you don't meet the current pilot program criteria. However, you can still 
                    save significantly with our standard pricing!
                  </p>
                  
                  <div style={{
                    background: '#FEF3C7',
                    padding: 20,
                    borderRadius: 12,
                    marginBottom: 32,
                    textAlign: 'left'
                  }}>
                    <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 12, color: '#92400E' }}>
                      Pilot Program Requirements:
                    </h3>
                    <ul style={{ margin: 0, paddingLeft: 20, color: '#92400E', lineHeight: 1.8, fontSize: 14 }}>
                      <li>Age 25-64</li>
                      <li>US resident (any state)</li>
                      <li>No Medicare coverage</li>
                      <li>2+ regular prescriptions</li>
                    </ul>
                  </div>
                  
                  <div style={{
                    background: '#EFF6FF',
                    padding: 20,
                    borderRadius: 12,
                    marginBottom: 32,
                    textAlign: 'left'
                  }}>
                    <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 12, color: '#1E40AF' }}>
                      Standard Membership Benefits:
                    </h3>
                    <ul style={{ margin: 0, paddingLeft: 20, color: '#3B82F6', lineHeight: 1.8, fontSize: 14 }}>
                      <li>~80% average savings vs retail</li>
                      <li>Free standard shipping (2-3 days)</li>
                      <li>Licensed pharmacist support</li>
                      <li>HIPAA-compliant platform</li>
                    </ul>
                  </div>
                  
                  <div style={{ display: 'flex', gap: 12, flexDirection: 'column' }}>
                    <Button fullWidth icon="arrowRight" onClick={() => onNavigate('register')}>
                      Sign Up for Standard Membership
                    </Button>
                    <Button fullWidth variant="secondary" onClick={() => setStep(1)}>
                      Re-check Eligibility
                    </Button>
                  </div>
                </>
              )}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
